import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { chatAnalytics } from '@/lib/analytics';
import {
  BarChart,
  MessageSquare,
  Clock,
  AlertCircle,
  TrendingUp
} from 'lucide-react';

interface DashboardMetric {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  description: string;
}

const ChatDashboard = () => {
  const [metrics, setMetrics] = useState(chatAnalytics.getMetrics());
  const [commonQueries, setCommonQueries] = useState(chatAnalytics.getCommonQueries());
  const [errorRate, setErrorRate] = useState(chatAnalytics.getErrorRate());

  useEffect(() => {
    // Actualizar métricas cada 5 segundos
    const interval = setInterval(() => {
      setMetrics(chatAnalytics.getMetrics());
      setCommonQueries(chatAnalytics.getCommonQueries());
      setErrorRate(chatAnalytics.getErrorRate());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const dashboardMetrics: DashboardMetric[] = [
    {
      title: 'Total de Mensajes',
      value: metrics.messageCount,
      icon: <MessageSquare className="w-8 h-8 text-primary" />,
      description: 'Mensajes procesados'
    },
    {
      title: 'Tiempo de Respuesta',
      value: `${metrics.averageResponseTime.toFixed(2)}ms`,
      icon: <Clock className="w-8 h-8 text-primary" />,
      description: 'Promedio'
    },
    {
      title: 'Tasa de Error',
      value: `${errorRate.toFixed(1)}%`,
      icon: <AlertCircle className="w-8 h-8 text-primary" />,
      description: 'Respuestas fallidas'
    },
    {
      title: 'Consultas Únicas',
      value: Object.keys(metrics.commonQueries).length,
      icon: <TrendingUp className="w-8 h-8 text-primary" />,
      description: 'Tipos de preguntas'
    }
  ];

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold mb-6">Dashboard del Chatbot</h2>
      
      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {dashboardMetrics.map((metric, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {metric.title}
                </p>
                <h3 className="text-2xl font-bold mt-2">{metric.value}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {metric.description}
                </p>
              </div>
              {metric.icon}
            </div>
          </Card>
        ))}
      </div>

      {/* Consultas Comunes */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-4">Consultas más Frecuentes</h3>
        <div className="space-y-4">
          {commonQueries.map((query, index) => (
            <div key={index} className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{query.query}</p>
              <span className="text-sm font-medium">{query.count} veces</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default ChatDashboard; 